/**
 * Claims Lifecycle Workspace — portfolio-wide claims with Bayesian settlement prediction + Cognara counter-offer drafter.
 */
import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Gavel, Sparkles, Loader2, ArrowLeft, TrendingUp, TrendingDown, Search } from 'lucide-react';
import api from '@/lib/api';

const fmtINR = (v) => v == null ? '—' : (Math.abs(v) >= 1e7 ? `₹${(v / 1e7).toFixed(2)} Cr` : Math.abs(v) >= 1e5 ? `₹${(v / 1e5).toFixed(2)} L` : `₹${Number(v).toLocaleString('en-IN')}`);
const statusColor = (s) => ({ DRAFT: '#94A3B8', SUBMITTED: '#3B82F6', UNDER_ASSESSMENT: '#F59E0B', IN_NEGOTIATION: '#F97316', SETTLED: '#22C55E', REJECTED: '#EF4444', CLOSED: '#6B7280' })[s] || '#94A3B8';

export default function ContractsClaimsWorkspace() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selected, setSelected] = useState(null);
  const [showOffer, setShowOffer] = useState(false);
  const [drafting, setDrafting] = useState(false);
  const [offer, setOffer] = useState(null);

  useEffect(() => { (async () => {
    try { const r = await api.get('/contracts/portfolio/claims'); setData(r.data); }
    catch { toast.error('Failed to load claims'); } finally { setLoading(false); }
  })(); }, []);

  const rows = data?.claims || [];
  const filtered = useMemo(() => rows.filter(r => {
    const t = q.toLowerCase();
    const okQ = !t || (r.title || '').toLowerCase().includes(t) || (r.claim_number || '').toLowerCase().includes(t) || (r.project || '').toLowerCase().includes(t);
    const okS = statusFilter === 'all' || r.status === statusFilter;
    return okQ && okS;
  }), [rows, q, statusFilter]);

  const draftOffer = async (claim) => {
    setSelected(claim); setShowOffer(true); setOffer(null); setDrafting(true);
    try { const r = await api.post(`/contracts/claims/${claim.id}/draft-counter-offer`); setOffer(r.data); toast.success('Counter-offer drafted'); }
    catch { toast.error('Draft failed'); } finally { setDrafting(false); }
  };

  const c = data?.counts || {}; const totals = data?.totals || {};

  if (loading) return <DashboardLayout><div className="flex items-center justify-center h-96"><Loader2 className="w-8 h-8 animate-spin text-blue-500" /></div></DashboardLayout>;

  return (
    <DashboardLayout>
      <div className="space-y-4" data-testid="contracts-claims-workspace">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/contracts')} data-testid="claims-back-btn"><ArrowLeft className="w-4 h-4" /></Button>
          <div>
            <h1 className="text-2xl font-bold text-foreground">Claims Lifecycle Workspace</h1>
            <p className="text-sm text-muted-foreground mt-1">Every claim · negotiation timeline · Bayesian settlement prediction · Cognara counter-offer drafter</p>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="claims-kpis">
          {[
            { label: 'Claims', value: c.total || 0, sub: `${c.open || 0} open`, color: '#94A3B8' },
            { label: 'In Negotiation', value: c.in_negotiation || 0, sub: 'Active', color: '#F97316' },
            { label: 'Settled', value: c.settled || 0, sub: 'Closed', color: '#22C55E' },
            { label: 'Total Requested', value: fmtINR(totals.requested), sub: 'Gross ask', color: '#EF4444' },
            { label: 'Predicted Settlement', value: fmtINR(totals.predicted_settlement), sub: `Save ${fmtINR(totals.predicted_savings)}`, color: '#8B5CF6' },
          ].map(kpi => (
            <Card key={kpi.label} className="bg-card border-border"><CardContent className="p-3">
              <p className="text-[10px] text-muted-foreground uppercase tracking-wider">{kpi.label}</p>
              <p className="text-xl font-bold" style={{ color: kpi.color }}>{kpi.value}</p>
              <p className="text-[10px] text-muted-foreground">{kpi.sub}</p>
            </CardContent></Card>
          ))}
        </div>

        <Card className="bg-card border-border">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3 flex-wrap">
              <div className="relative flex-1 min-w-[200px] max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Search title / number / project..." className="pl-10 bg-muted border-border text-foreground" data-testid="claims-search" />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-52 bg-muted border-border text-foreground" data-testid="claims-status-filter"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-card border-border">
                  {['all', 'DRAFT', 'SUBMITTED', 'UNDER_ASSESSMENT', 'IN_NEGOTIATION', 'SETTLED', 'REJECTED'].map(s => <SelectItem key={s} value={s}>{s === 'all' ? 'All Status' : s.replace(/_/g, ' ')}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="claims-table">
                <thead className="text-muted-foreground uppercase text-[10px]">
                  <tr>{['Claim', 'Type', 'Project', 'Requested (₹/days)', 'Predicted (₹/days)', 'Recovery %', 'Rounds', 'Status', 'Action'].map(h => <th key={h} className="text-left px-2 py-2">{h}</th>)}</tr>
                </thead>
                <tbody>
                  {filtered.map(r => (
                    <tr key={r.id} className="border-t border-border hover:bg-white/[0.02]" data-testid={`claim-row-${r.id}`}>
                      <td className="px-2 py-2 text-foreground">{r.title}<div className="text-[10px] text-muted-foreground">{r.claim_number}</div></td>
                      <td className="px-2 py-2"><Badge variant="outline" className="text-[10px]">{r.claim_type}</Badge></td>
                      <td className="px-2 py-2 text-muted-foreground">{r.project}</td>
                      <td className="px-2 py-2 text-red-400">{fmtINR(r.requested_amount)} / {r.requested_days}d</td>
                      <td className="px-2 py-2 text-purple-400">{fmtINR(r.prediction?.predicted_settlement_amount)} / {r.prediction?.predicted_settlement_days}d</td>
                      <td className="px-2 py-2 font-semibold" style={{ color: (r.prediction?.recovery_probability_pct || 0) >= 60 ? '#22C55E' : (r.prediction?.recovery_probability_pct || 0) >= 30 ? '#F59E0B' : '#EF4444' }}>{r.prediction?.recovery_probability_pct}%</td>
                      <td className="px-2 py-2 text-foreground/80">{r.negotiations_count}</td>
                      <td className="px-2 py-2"><Badge style={{ background: `${statusColor(r.status)}22`, color: statusColor(r.status) }} className="text-[9px]">{r.status}</Badge></td>
                      <td className="px-2 py-2"><Button size="sm" variant="ghost" onClick={() => draftOffer(r)} className="h-6 px-2 text-purple-400 hover:text-purple-300" data-testid={`claim-offer-${r.id}`}><Sparkles className="w-3 h-3 mr-1" />Counter-offer</Button></td>
                    </tr>
                  ))}
                  {filtered.length === 0 && <tr><td colSpan={9} className="text-center py-6 text-muted-foreground">No claims match filters</td></tr>}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        <Dialog open={showOffer} onOpenChange={setShowOffer}>
          <DialogContent className="max-w-2xl bg-card border-border text-foreground" data-testid="claim-offer-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><Sparkles className="w-4 h-4 text-purple-400" />Cognara Counter-Offer — {selected?.claim_number}</DialogTitle>
              <DialogDescription>Bayesian-anchored counter-offer with legal basis.</DialogDescription>
            </DialogHeader>
            {drafting ? (
              <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-purple-500 mr-2" /><span className="text-sm text-muted-foreground">Drafting...</span></div>
            ) : offer ? (
              <div className="space-y-3 text-sm" data-testid="claim-offer-content">
                <div className="grid grid-cols-2 gap-2">
                  <Card className="bg-muted border-border"><CardContent className="p-3"><p className="text-[10px] text-muted-foreground uppercase">Counter Amount</p><p className="text-xl font-bold text-emerald-400">{fmtINR(offer.counter_amount)}</p></CardContent></Card>
                  <Card className="bg-muted border-border"><CardContent className="p-3"><p className="text-[10px] text-muted-foreground uppercase">Counter Days</p><p className="text-xl font-bold text-blue-400">{offer.counter_days}d</p></CardContent></Card>
                </div>
                <div className="rounded p-3" style={{ background: 'var(--muted)', border: '1px solid var(--border)' }}>
                  <p className="text-muted-foreground text-xs uppercase mb-1">Subject</p>
                  <p className="text-foreground font-semibold">{offer.subject}</p>
                </div>
                <div className="rounded p-3 max-h-64 overflow-y-auto whitespace-pre-wrap text-foreground" style={{ background: 'var(--muted)', border: '1px solid var(--border)' }}>{offer.body}</div>
                {offer.rationale_bullets?.length > 0 && <div>
                  <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Rationale</p>
                  <ul className="list-disc list-inside text-xs text-foreground/80 space-y-0.5">{offer.rationale_bullets.map((b, i) => <li key={i}>{b}</li>)}</ul>
                </div>}
                {offer.legal_basis?.length > 0 && <div>
                  <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Legal Basis</p>
                  <div className="flex flex-wrap gap-1">{offer.legal_basis.map((c, i) => <Badge key={i} variant="outline" className="text-[9px]">{c}</Badge>)}</div>
                </div>}
              </div>
            ) : null}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
